import * as React from "react";

import { cn } from "../../../utils";

export type TOTPInputProps = {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  error?: boolean;
};

const OTPInput = React.forwardRef<HTMLDivElement, TOTPInputProps>(
  ({ value, onChange, onComplete, className, disabled, error }, ref) => {
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);
    const [focusedIndex, setFocusedIndex] = React.useState<number | null>(null);

    const otpLength = 6;
    const otpArray = value.split("").slice(0, otpLength);
    while (otpArray.length < otpLength) {
      otpArray.push("");
    }

    const handleChange = (index: number, newValue: string) => {
      // Only allow numeric input
      const numericValue = newValue.replace(/\D/g, "");
      if (!numericValue) {
        const cleared = [...otpArray];
        cleared[index] = "";
        onChange(cleared.join(""));
        return;
      }

      // Distribute typed digits forward from this box. Handles fast typing
      // (multiple digits landing in one change event) and typing over a
      // filled box (the new digit replaces it and the rest shift forward).
      const newOtpArray = [...otpArray];
      let cursor = index;
      for (const char of numericValue) {
        if (cursor >= otpLength) break;
        newOtpArray[cursor] = char;
        cursor += 1;
      }
      const newOtp = newOtpArray.join("");

      onChange(newOtp);

      // Auto-focus the next empty field (or the last one we filled)
      const nextIndex = Math.min(cursor, otpLength - 1);
      inputRefs.current[nextIndex]?.focus();
      setFocusedIndex(nextIndex);

      // Call onComplete when all fields are filled
      if (newOtp.length === otpLength && onComplete) {
        onComplete(newOtp);
      }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace") {
        if (!otpArray[index] && index > 0) {
          // If current field is empty, focus previous field
          inputRefs.current[index - 1]?.focus();
          setFocusedIndex(index - 1);
        } else {
          // Clear current field
          const newOtpArray = [...otpArray];
          newOtpArray[index] = "";
          onChange(newOtpArray.join(""));
        }
      } else if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      } else if (e.key === "ArrowRight" && index < otpLength - 1) {
        inputRefs.current[index + 1]?.focus();
        setFocusedIndex(index + 1);
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, otpLength);
      if (pastedData.length > 0) {
        const newOtpArray = [...otpArray];
        for (let i = 0; i < pastedData.length && i < otpLength; i++) {
          newOtpArray[i] = pastedData[i];
        }
        const newOtp = newOtpArray.join("");
        onChange(newOtp);

        // Focus the next empty field or the last field
        const nextIndex = Math.min(pastedData.length, otpLength - 1);
        inputRefs.current[nextIndex]?.focus();
        setFocusedIndex(nextIndex);

        // Call onComplete if all fields are filled
        if (newOtp.length === otpLength && onComplete) {
          onComplete(newOtp);
        }
      }
    };

    const handleFocus = (index: number) => {
      setFocusedIndex(index);
      // Select any existing digit so typing replaces it instead of being
      // silently blocked by maxLength.
      inputRefs.current[index]?.select();
    };

    const handleBlur = () => {
      setFocusedIndex(null);
    };

    return (
      <div
        ref={ref}
        className={cn("flex gap-4", className)}
      >
        {otpArray.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={() => handleFocus(index)}
            onBlur={handleBlur}
            disabled={disabled}
            className={cn(
              "flex h-14 w-14 rounded-xl border bg-muted text-center text-lg font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              error ? "border-destructive focus-visible:ring-destructive" : "border-border focus-visible:ring-ring",
              focusedIndex === index && "ring-2 ring-ring"
            )}
            aria-label={`OTP digit ${index + 1}`}
          />
        ))}
      </div>
    );
  }
);

OTPInput.displayName = "OTPInput";

export { OTPInput };
